import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { requestPasswordReset } from '@/services/authService';

type Status = 'idle' | 'sending' | 'sent' | 'error';

const RESPONSE_MESSAGE_KEY_MAP: Record<string, string> = {
  'If an account exists for this email, a password reset link has been sent.': 'forgotPassword.sentMessage',
  'If an account exists, a reset link has been sent.': 'forgotPassword.sentMessage',
};

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [messageKey, setMessageKey] = useState<string | null>(null);
  const [messageText, setMessageText] = useState<string>('');
  const message = messageKey ? t(messageKey) : messageText;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email || status === 'sending') {
      return;
    }

    setStatus('sending');
    setMessageKey(null);
    setMessageText('');
    try {
      const response = await requestPasswordReset(email);
      setStatus('sent');
      if (response.message) {
        const key = RESPONSE_MESSAGE_KEY_MAP[response.message];
        if (key) {
          setMessageKey(key);
          setMessageText('');
        } else {
          setMessageKey(null);
          setMessageText(response.message);
        }
      } else {
        setMessageKey('forgotPassword.sentMessage');
      }
    } catch (error: any) {
      setStatus('error');
      const responseMessage = error.response?.data?.message;
      if (responseMessage) {
        setMessageKey(null);
        setMessageText(responseMessage);
      } else {
        setMessageKey('common.unexpectedError');
      }
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl p-8">
        <h1 className="text-3xl font-semibold mb-4">{t('forgotPassword.title')}</h1>
        <p className="text-slate-200 mb-6">{t('forgotPassword.description')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('forgotPassword.emailPlaceholder')}
            type="email"
            required
          />
          <Button type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? t('forgotPassword.sending') : t('forgotPassword.submitButton')}
          </Button>
        </form>

        {message && (
          <p className={`mt-4 text-sm ${status === 'error' ? 'text-red-300' : 'text-green-300'}`}>
            {message}
          </p>
        )}

        <div className="mt-6">
          <Link to="/studio" className="text-sm text-indigo-300 hover:text-indigo-200">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
